import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../context/ThemeContext';
import { getStyleWithError } from '../../utils/styles';
import { isAppleSignInAvailable } from '../../services/socialAuth';
import { isWeb } from '../../utils/platformUtils';

export type AuthSignInFormErrors = {
  email: string;
  password: string;
};

type Props = {
  email: string;
  onEmailChange: (value: string) => void;
  password: string;
  onPasswordChange: (value: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  validationErrors: AuthSignInFormErrors;
  isLoading: boolean;
  socialLoading?: 'google' | 'apple' | null;
  onSignIn: () => void;
  onGoogleSignIn: () => void;
  onAppleSignIn: () => void;
  onForgotPassword: () => void;
  onCreateAccount: () => void;
  socialError?: string;
  /** Native welcome: form sits on header-colored panel (Android/iOS). */
  onDarkPanel?: boolean;
};

const AuthSignInForm = ({
  email,
  onEmailChange,
  password,
  onPasswordChange,
  showPassword,
  onTogglePassword,
  validationErrors,
  isLoading,
  socialLoading = null,
  onSignIn,
  onGoogleSignIn,
  onAppleSignIn,
  onForgotPassword,
  onCreateAccount,
  socialError = '',
  onDarkPanel = false,
}: Props) => {
  const { colors } = useTheme();
  const showApple = isAppleSignInAvailable();
  const socialBusy = Boolean(socialLoading);
  const formBusy = isLoading || socialBusy;

  const labelColor = onDarkPanel ? 'rgba(255,255,255,0.75)' : colors.text.secondary;
  const inputBg = onDarkPanel ? '#FFFFFF' : colors.surface;
  const socialBg = onDarkPanel ? 'rgba(255,255,255,0.06)' : colors.surface;
  const socialBorder = onDarkPanel ? 'rgba(255,255,255,0.22)' : colors.border;

  const googleButtonColors =
    onDarkPanel
      ? { backgroundColor: socialBg, borderColor: socialBorder }
      : isWeb
        ? {
            backgroundColor: 'transparent',
            borderColor: colors.primary.dark,
          }
        : { backgroundColor: colors.surface, borderColor: colors.border };

  return (
    <View style={styles.form}>
      <View style={styles.field}>
        <Text style={[styles.label, { color: labelColor }]}>Email address</Text>
        <TextInput
          style={getStyleWithError(
            [styles.input, { borderColor: colors.border, color: colors.text.primary, backgroundColor: inputBg }],
            validationErrors.email,
            styles.inputError
          )}
          placeholder="you@example.com"
          placeholderTextColor={colors.text.placeholder}
          value={email}
          onChangeText={onEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="email"
        />
        {validationErrors.email ? (
          <Text style={[styles.errorText, { color: colors.error.main }]}>{validationErrors.email}</Text>
        ) : null}
      </View>

      <View style={styles.field}>
        <Text style={[styles.label, { color: labelColor }]}>Password</Text>
        <View style={styles.passwordRow}>
          <TextInput
            style={getStyleWithError(
              [
                styles.input,
                styles.passwordInput,
                { borderColor: colors.border, color: colors.text.primary, backgroundColor: inputBg },
              ],
              validationErrors.password,
              styles.inputError
            )}
            placeholder="Enter your password"
            placeholderTextColor={colors.text.placeholder}
            value={password}
            onChangeText={onPasswordChange}
            secureTextEntry={!showPassword}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="password"
          />
          <TouchableOpacity
            onPress={onTogglePassword}
            style={styles.eyeButton}
            accessibilityRole="button"
            accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
          >
            <Ionicons
              name={showPassword ? 'eye-outline' : 'eye-off-outline'}
              size={22}
              color={colors.grey[500]}
            />
          </TouchableOpacity>
        </View>
        {validationErrors.password ? (
          <Text style={[styles.errorText, { color: colors.error.main }]}>{validationErrors.password}</Text>
        ) : null}
      </View>

      <TouchableOpacity onPress={onForgotPassword} style={styles.forgotRow} accessibilityRole="button">
        <Text style={[styles.forgotText, { color: colors.accent.mint }]}>Forgot password?</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.primaryButton,
          onDarkPanel
            ? { backgroundColor: 'rgba(255,255,255,0.12)', borderWidth: 1, borderColor: colors.accent.mint }
            : { backgroundColor: colors.primary.header },
          isLoading && styles.buttonDisabled,
        ]}
        onPress={onSignIn}
        disabled={formBusy}
        accessibilityRole="button"
      >
        {isLoading ? (
          <ActivityIndicator color={colors.accent.mint} size="small" />
        ) : (
          <Text style={[styles.primaryButtonText, { color: colors.accent.mint }]}>Sign in</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.secondaryButton,
          onDarkPanel
            ? { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.22)' }
            : { backgroundColor: colors.grey[100], borderColor: colors.border },
        ]}
        onPress={onCreateAccount}
        accessibilityRole="button"
      >
        <Text
          style={[
            styles.secondaryButtonText,
            { color: onDarkPanel ? 'rgba(255,255,255,0.85)' : colors.text.secondary },
          ]}
        >
          Create account
        </Text>
      </TouchableOpacity>

      {socialError ? (
        <Text style={[styles.socialError, { color: colors.error.main }]}>{socialError}</Text>
      ) : null}

      <View style={[styles.socialRow, !showApple && styles.socialRowSingle]}>
        <TouchableOpacity
          style={[
            styles.socialButton,
            !showApple && styles.socialButtonFull,
            googleButtonColors,
            isWeb && !onDarkPanel && styles.socialButtonWeb,
          ]}
          onPress={onGoogleSignIn}
          disabled={formBusy}
          accessibilityRole="button"
        >
          {socialLoading === 'google' ? (
            <ActivityIndicator size="small" color={onDarkPanel ? '#FFFFFF' : colors.text.primary} />
          ) : (
            <Text
              style={[
                styles.socialButtonText,
                { color: onDarkPanel ? '#FFFFFF' : colors.text.primary },
              ]}
            >
              Google
            </Text>
          )}
        </TouchableOpacity>
        {showApple ? (
          <TouchableOpacity
            style={[
              styles.socialButton,
              onDarkPanel
                ? { backgroundColor: socialBg, borderColor: socialBorder }
                : { backgroundColor: colors.surface, borderColor: colors.border },
            ]}
            onPress={onAppleSignIn}
            disabled={formBusy}
            accessibilityRole="button"
          >
            {socialLoading === 'apple' ? (
              <ActivityIndicator size="small" color={onDarkPanel ? '#FFFFFF' : colors.text.primary} />
            ) : (
              <Text
                style={[
                  styles.socialButtonText,
                  { color: onDarkPanel ? '#FFFFFF' : colors.text.primary },
                ]}
              >
                Apple
              </Text>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  form: {
    width: '100%',
    gap: 0,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'web' ? 14 : 12,
    fontSize: 16,
    width: '100%',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as const } : {}),
  },
  inputError: {
    borderColor: '#EF4444',
  },
  passwordRow: {
    position: 'relative',
    width: '100%',
  },
  passwordInput: {
    paddingRight: 48,
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 12,
    marginTop: 6,
  },
  forgotRow: {
    alignSelf: 'flex-end',
    marginTop: -4,
    marginBottom: 20,
  },
  forgotText: {
    fontSize: 14,
    fontWeight: '500',
  },
  primaryButton: {
    height: 52,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  secondaryButton: {
    height: 52,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    marginBottom: 20,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  socialError: {
    fontSize: 13,
    marginBottom: 12,
    textAlign: 'center',
  },
  socialRow: {
    flexDirection: 'row',
    gap: 12,
  },
  socialRowSingle: {
    gap: 0,
  },
  socialButtonFull: {
    flex: 1,
    maxWidth: '100%',
  },
  socialButton: {
    flex: 1,
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  socialButtonWeb: {
    borderWidth: 2,
    ...(Platform.OS === 'web'
      ? {
          cursor: 'pointer',
        }
      : {}),
  } as const,
  socialButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
});

export default AuthSignInForm;
